import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useTranslations } from 'gt-react'
import { useMemo } from 'react'
import { EntityList, type EntityListColumn } from '@/components/crm/entity-list'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/crm/contacts/')({
  component: ContactsIndexPage,
})

type ContactRow = {
  id: string
  firstName?: string | null
  lastName?: string | null
  email?: string | null
  jobTitle?: string | null
  accountId?: string | null
  ownerUserId?: string | null
}

type AccountRow = { id: string; name?: string | null }

function fullName(r: ContactRow): string {
  return `${r.firstName ?? ''} ${r.lastName ?? ''}`.trim() || r.email || '(no name)'
}

function ContactsIndexPage() {
  const t = useTranslations()
  const navigate = useNavigate()

  const { data, isLoading } = useQuery(
    orpc.crm.contacts.list.queryOptions({ input: { limit: 500, offset: 0 } }),
  )
  const rows: ContactRow[] = (data as ContactRow[] | undefined) ?? []

  // Account names are looked up via the list query and joined client-side.
  // This keeps the contacts-list backend procedure untouched (it returns no
  // account info) while still rendering a readable "Account" column.
  const { data: accountData } = useQuery(
    orpc.crm.accounts.list.queryOptions({ input: { limit: 500, offset: 0 } }),
  )
  const accountMap = useMemo(() => {
    const m = new Map<string, string>()
    const all = (accountData as AccountRow[] | undefined) ?? []
    for (const a of all) if (a.name) m.set(a.id, a.name)
    return m
  }, [accountData])

  const columns: EntityListColumn<ContactRow>[] = [
    {
      key: 'name',
      header: t('crm.account.name'),
      render: (r) => <span className="font-medium">{fullName(r)}</span>,
    },
    { key: 'email', header: t('crm.contact.email'), render: (r) => r.email ?? '-' },
    { key: 'jobTitle', header: t('crm.contact.jobTitle'), render: (r) => r.jobTitle ?? '-' },
    {
      key: 'account',
      header: t('crm.contact.account'),
      render: (r) => (r.accountId ? (accountMap.get(r.accountId) ?? '-') : '-'),
    },
    { key: 'owner', header: t('crm.common.ownedBy'), render: (r) => r.ownerUserId ?? '-' },
  ]

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t('crm.contact.entityPlural')}</h1>
      </div>

      <EntityList
        rows={rows}
        columns={columns}
        searchAccessor={(r) => `${fullName(r)} ${r.email ?? ''} ${r.jobTitle ?? ''}`}
        searchPlaceholder={`${t('crm.common.search')}…`}
        isLoading={isLoading}
        newButton={{
          label: t('crm.contact.newContact'),
          onClick: () => navigate({ to: '/crm/contacts/new' }),
        }}
        onRowClick={(r) =>
          navigate({ to: '/crm/contacts/$contactId', params: { contactId: r.id } })
        }
      />
    </div>
  )
}
